import React, { useReducer, ChangeEvent, useState, useMemo } from 'react';
import { Box, MenuItem, TableCell, Tooltip, Typography, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';

import { useTranslation } from 'react-i18next';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { IconChevronDown } from '@shared/assets/images/svgs';
import { useStyles } from './styles';
import { useStyles as useSharedStyles } from '../../styles';
import {
  AgentDto,
  TranscriberResponseDto,
  useAgentProfileListControllerAgentProfileListForUsersQuery,
  useCompanyControllerGetCompanyQuery,
  useLlmModelListControllerModelListQuery,
} from '@shared/services/apiService/apiService';
import { useEffect } from 'react';
import { useCreateAgent } from '@features/Admin/Agent/hooks/useCreateAgent';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '@shared/store';
import { selectors } from '@shared/store/ducks';
import { AgentProfileRequestDto } from '@features/Admin/Agent/model/types';
import { accentOptions, DEFAULT_NO_ROLE_AGENT_PROFILE, mapLanguageToISO639 } from './constants';
import { toast } from 'react-toastify';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { RolePicker } from './RolePicker';
import { VoicePicker } from './VoicePicker';

interface State {
  agent: string;
  sex: string;
  voice?: string;
  language: string;
  transcriberId: string;
  profile?: AgentProfileRequestDto;
}
enum ActionEnum {
  SET_AGENT = 'SET_AGENT',
  SET_SEX = 'SET_SEX',
  SET_VOICE = 'SET_VOICE',
  SET_LANGUAGE = 'SET_LANGUAGE',
  SET_TRANSCRIBER = 'SET_TRANSCRIBER',
  SET_PROFILE = 'SET_PROFILE',
  RESET = 'RESET',
}

type Action =
  | { type: ActionEnum.SET_AGENT; payload: string }
  | { type: ActionEnum.SET_SEX; payload: string }
  | { type: ActionEnum.SET_VOICE; payload?: string }
  | { type: ActionEnum.SET_LANGUAGE; payload: string }
  | { type: ActionEnum.SET_TRANSCRIBER; payload: string }
  | { type: ActionEnum.SET_PROFILE; payload?: AgentProfileRequestDto }
  | { type: ActionEnum.RESET };

const initialAgentState: State = {
  agent: '',
  sex: '',
  voice: '',
  language: '',
  transcriberId: '',
  profile: {
    name: '',
  },
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionEnum.SET_AGENT:
      return { ...state, agent: action.payload };
    case ActionEnum.SET_SEX:
      return { ...state, sex: action.payload };
    case ActionEnum.SET_VOICE:
      return { ...state, voice: action.payload };
    case ActionEnum.SET_TRANSCRIBER:
      return { ...state, transcriberId: action.payload };
    case ActionEnum.SET_LANGUAGE:
      return { ...state, language: action.payload };
    case ActionEnum.SET_PROFILE:
      return { ...state, profile: action.payload };
    case ActionEnum.RESET:
      return initialAgentState;
    default:
      return state;
  }
};

const CompositeFieldContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 0 }}>
      {children}
    </Box>
  );
};

const CustomSelectField: React.FC<{
  control: any;
  labelId: string;
  name: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement | { value: unknown }>) => void;
  placeholder: string;
  options: { key: string; value: string; label: string; icon?: React.ReactNode }[];
  disabled?: boolean;
  renderValue?: (selected: any) => React.ReactNode;
  className?: string;
}> = ({
  control,
  labelId,
  name,
  value,
  onChange,
  placeholder,
  options,
  disabled,
  renderValue,
  className,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();

  const defaultRenderValue = (value: string) => {
    if (!value) {
      return <Typography color={theme.palette.extendedGrey?.neutral}>{placeholder}</Typography>;
    }
    return <>{value}</>;
  };

  return (
    <CustomSelect
      control={control}
      labelId={labelId}
      name={name}
      onChange={onChange}
      id={`${labelId}-id`}
      disabled={disabled}
      value={value}
      IconComponent={IconChevronDown}
      className={className || classes.select}
      displayEmpty
      renderValue={renderValue ? renderValue : defaultRenderValue}>
      {options.map((option) => (
        <MenuItem key={option.key} value={option.value}>
          <Box className={classes.selectMenuItemContent}>
            {option.icon}
            {!option.icon && option.label}
          </Box>
        </MenuItem>
      ))}
    </CustomSelect>
  );
};

interface ManageAgentProps {
  onClose: () => Promise<void> | void;
  onCreated?: () => Promise<void>;
  agents: AgentDto[];
  agent: AgentDto | null;
  squadId?: string | null;
  transcriberData?: TranscriberResponseDto[];
}

export const ManageAgent: React.FC<ManageAgentProps> = ({
  onClose,
  onCreated,
  agents,
  agent,
  squadId,
  transcriberData,
}) => {
  const [state, dispatch] = useReducer(
    reducer,
    agent
      ? ({
          agent: agent.name,
          sex: agent.voice?.gender,
          language: mapLanguageToISO639((agent.languages?.length && agent?.languages[0]) || ''),
          transcriberId: agent.transcriberId || agent.transcriber?.id || '',
          voice: agent.voice?.id,
          profile: agent.profile || DEFAULT_NO_ROLE_AGENT_PROFILE,
        } as State)
      : initialAgentState,
  );

  const user = useAppSelector(selectors.auth.getUser);
  const companyId = user?.companyId ?? '';
  const { data: company } = useCompanyControllerGetCompanyQuery(
    { id: companyId },
    { skip: !companyId, refetchOnMountOrArgChange: true },
  );
  const voiceData = company?.voices;
  const llmProviders = company?.llmProviders;

  if (!user?.companyId) {
    return <div>User not found...</div>;
  }

  const { classes } = useStyles();
  const { classes: sharedClasses } = useSharedStyles();

  const { campaignId } = useParams();

  const {
    control,
    isLoading: isCreatingAgent,
    setValue,
    onSubmit,
  } = useCreateAgent(campaignId || '', agents, squadId);
  const { t } = useTranslation();

  const { data: agentProfileTypes } = useAgentProfileListControllerAgentProfileListForUsersQuery({
    filter: 'system,custom',
  });

  const agentProfileSelectList = useMemo(
    () => agentProfileTypes?.concat([DEFAULT_NO_ROLE_AGENT_PROFILE]),
    [agentProfileTypes],
  );

  // LLM config local state
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [providerAllowsCustom, setProviderAllowsCustom] = useState<boolean>(false);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [customModelKey, setCustomModelKey] = useState<string>('');

  // Fetch models only when provider selected and it does NOT allow custom models
  const { data: llmModels } = useLlmModelListControllerModelListQuery(
    { id: selectedProviderId as string },
    { skip: !selectedProviderId || providerAllowsCustom },
  );

  const [isPlayingVoice, setIsPlayingVoice] = useState<boolean>(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);

  // Initialize form with agent data if editing
  useEffect(() => {
    if (agent) {
      dispatch({ type: ActionEnum.SET_AGENT, payload: agent.name });
      dispatch({ type: ActionEnum.SET_SEX, payload: agent.voice?.gender || '' });
      dispatch({ type: ActionEnum.SET_VOICE, payload: agent?.voice?.id || '' });
      dispatch({
        type: ActionEnum.SET_LANGUAGE,
        payload: mapLanguageToISO639((agent.languages?.length && agent?.languages[0]) || ''),
      });
      dispatch({
        type: ActionEnum.SET_PROFILE,
        payload:
          agentProfileSelectList?.find(
            (p) => (p.name as unknown as string) === (agent.profile?.name as unknown as string),
          ) || DEFAULT_NO_ROLE_AGENT_PROFILE,
      });
      dispatch({
        type: ActionEnum.SET_TRANSCRIBER,
        payload: agent?.transcriberId || agent?.transcriber?.id || '',
      });

      // Prefill LLM configuration
      if (llmProviders && agent.llmProviderId) {
        setSelectedProviderId(agent.llmProviderId);
        const provider = llmProviders.find((p) => p.id === agent.llmProviderId);
        const allowsCustom =
          provider?.allowsCustomModels ?? agent.llmProvider?.allowsCustomModels ?? false;
        setProviderAllowsCustom(allowsCustom);
        if (allowsCustom) {
          setCustomModelKey(agent.llmCustomModelKey || '');
          setSelectedModelId('');
        } else {
          setSelectedModelId(agent.llmModelId || '');
          setCustomModelKey('');
        }
      }
    } else {
      dispatch({ type: ActionEnum.RESET });
      if (llmProviders && llmProviders.length > 0) {
        const firstProvider = llmProviders[0];
        setSelectedProviderId(firstProvider.id);
        setProviderAllowsCustom(firstProvider.allowsCustomModels);
        setSelectedModelId('');
        setCustomModelKey('');
      } else {
        setSelectedProviderId('');
        setProviderAllowsCustom(false);
        setSelectedModelId('');
        setCustomModelKey('');
      }
    }
  }, [agent, agentProfileSelectList, llmProviders]);

  useEffect(() => {
    if (!llmProviders?.length) {
      if (selectedProviderId) {
        setSelectedProviderId('');
        setProviderAllowsCustom(false);
        setSelectedModelId('');
        setCustomModelKey('');
      }
      return;
    }
    if (selectedProviderId && llmProviders.some((p) => p.id === selectedProviderId)) {
      return;
    }
    const fallback = llmProviders[0];
    setSelectedProviderId(fallback.id);
    setProviderAllowsCustom(fallback.allowsCustomModels);
    setSelectedModelId('');
    setCustomModelKey('');
  }, [llmProviders, selectedProviderId]);

  useEffect(() => {
    if (!agent && !providerAllowsCustom && llmModels && llmModels.length > 0 && !selectedModelId) {
      setSelectedModelId(llmModels[0].id);
    }
  }, [agent, providerAllowsCustom, llmModels, selectedModelId]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    };
  }, [currentAudio]);

  const stopAudioPlayback = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setIsPlayingVoice(false);
      setCurrentAudio(null);
    }
  };

  const handleInputChange =
    (type: Action['type']) => (event: ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      switch (type) {
        case ActionEnum.SET_SEX:
          dispatch({ type: ActionEnum.SET_SEX, payload: event.target.value as string });
          dispatch({ type: ActionEnum.SET_VOICE, payload: undefined });
          return;

        case ActionEnum.SET_VOICE:
          dispatch({
            type: ActionEnum.SET_VOICE,
            payload: event.target.value as string,
          });
          return;

        case ActionEnum.SET_AGENT:
        case ActionEnum.SET_LANGUAGE:
          dispatch({ type, payload: event.target.value as string });
          return;

        case ActionEnum.SET_TRANSCRIBER:
          dispatch({ type, payload: event.target.value as string });
          return;

        default:
          // no need to handle RESET
          return;
      }
    };

  const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    stopAudioPlayback();

    setValue('name', state.agent);
    setValue('voiceId', state?.voice);
    setValue('transcriberId', state?.transcriberId); // TODO: to be implemented CLARIFY WITH MAXIM
    setValue('languages', [mapLanguageToISO639(state.language)]);
    setValue('profile', state.profile);
    setValue('profileId', state.profile?.id);

    // Map LLM config to form values according to rules
    setValue('llmProviderId', selectedProviderId);
    if (providerAllowsCustom) {
      // If provider allows custom models -> send only custom key
      setValue('llmCustomModelKey', customModelKey || null);
      setValue('llmModelId', null);
    } else {
      // If provider does not allow custom models -> send model id
      setValue('llmModelId', selectedModelId || null);
      setValue('llmCustomModelKey', null);
    }

    if (agent) {
      setValue('id', agent?.id || '');
    }

    await onSubmit().then(() => {
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });
    });
    dispatch({ type: ActionEnum.RESET });
    if (!agent && onCreated) {
      await onCreated();
      return;
    }
    await onClose();
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    stopAudioPlayback();

    onClose();
    dispatch({ type: ActionEnum.RESET });
  };

  const sexOptions = Array.from(new Set(voiceData?.map((voice) => voice.gender))).map((gender) => {
    const genderLower = (gender || '').toLowerCase();
    return {
      key: gender || '',
      value: gender || '',
      label: (gender ?? '').charAt(0).toUpperCase() + (gender ?? '').slice(1) || '',
      icon:
        genderLower === 'male' ? (
          <MaleIcon className={classes.maleIcon} />
        ) : genderLower === 'female' ? (
          <FemaleIcon className={classes.femaleIcon} />
        ) : null,
    };
  });

  const filteredVoices = useMemo(
    () => voiceData?.filter((voice) => voice.gender === state.sex) || [],
    [voiceData, state.sex],
  );

  const transcriberOptions =
    transcriberData?.map((transcriber) => {
      return {
        key: transcriber.id,
        value: transcriber.id,
        label: `${transcriber.provider} / ${transcriber.language}${transcriber.model ? ` / ${transcriber.model}` : ''}`,
      };
    }) || [];

  const playVoicePreview = (previewUrl: string) => {
    if (isPlayingVoice || !previewUrl) return;

    stopAudioPlayback();

    const audio = new Audio();
    let hasStartedPlaying = false;

    audio.onerror = () => {
      console.error('Failed to load voice preview:', previewUrl);
      setIsPlayingVoice(false);
      setCurrentAudio(null);
    };

    audio.onended = () => {
      setIsPlayingVoice(false);
      setCurrentAudio(null);
    };

    audio.oncanplaythrough = () => {
      if (!hasStartedPlaying) {
        hasStartedPlaying = true;
        audio.play().catch((error) => {
          console.error('Failed to play voice preview:', error);
          setIsPlayingVoice(false);
          setCurrentAudio(null);
        });
      }
    };

    setIsPlayingVoice(true);
    setCurrentAudio(audio);
    audio.src = previewUrl;
    audio.load();
  };

  const handleVoiceIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (isPlayingVoice && currentAudio) {
      stopAudioPlayback();
      return;
    }

    if (!state.sex || !state.voice) {
      console.warn('Please select a gender and voice first');
      return;
    }

    const selectedVoice = voiceData?.find((voice) => voice.id === state.voice);
    if (!selectedVoice?.previewUrl) {
      console.warn('No preview URL available for this voice');
      return;
    }

    playVoicePreview(selectedVoice.previewUrl);
  };

  const isSaveDisabled =
    isCreatingAgent ||
    !state.agent ||
    !state.sex ||
    !state.voice ||
    !state.language ||
    !state.transcriberId ||
    !state.profile ||
    // LLM validation rules:
    !selectedProviderId ||
    (!providerAllowsCustom && !selectedModelId) ||
    (providerAllowsCustom && !customModelKey?.trim());

  return (
    <>
      {squadId && <TableCell sx={{ padding: '8px' }} />}
      <TableCell className={sharedClasses.agentIdCell} sx={{ padding: '8px' }} />
      <TableCell className={sharedClasses.nameCell}>
        <CustomTextField
          control={control}
          name="name"
          value={state.agent}
          onChange={handleInputChange(ActionEnum.SET_AGENT)}
          id="agent-name"
          type="text"
          placeholder={t('campaignPage.placeholders.humanName')}
          varient="outlined"
          inputProps={{ maxLength: 56 }}
          fullWidth
          required
          disabled={isPlayingVoice}
        />
      </TableCell>

      <TableCell className={sharedClasses.voiceCell}>
        <CompositeFieldContainer>
          <Box sx={{ minWidth: 0 }}>
            <CustomSelectField
              control={control}
              labelId="sex-type"
              name="sexType"
              value={state.sex}
              onChange={handleInputChange(ActionEnum.SET_SEX)}
              placeholder={t('campaignPage.placeholders.sexType')}
              options={sexOptions}
              disabled={isPlayingVoice}
              className={classes.sexSelect}
              renderValue={(selected) => {
                const selectedSex = sexOptions?.find((option) => option.key === selected);
                return (
                  <Box className={classes.genderSelectContainer}>
                    {selectedSex?.icon || selectedSex?.label || ''}
                  </Box>
                );
              }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box className={classes.voiceContainer}>
              {(() => {
                const selectedVoice = voiceData?.find((voice) => voice.id === state.voice);
                const hasPreview = selectedVoice?.previewUrl;
                const canPlay = state.sex && state.voice && hasPreview;
                const isDisabled = !state.sex || !state.voice;

                const iconClasses = [
                  classes.voiceIcon,
                  isPlayingVoice
                    ? classes.voiceIconPlaying
                    : hasPreview
                      ? classes.voiceIconWithPreview
                      : classes.voiceIconNoPreview,
                  isDisabled ? classes.voiceIconDisabled : '',
                  isPlayingVoice ? classes.voiceIconAnimated : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <Tooltip
                    title={
                      isDisabled
                        ? t('campaignPage.placeholders.selectVoiceFirst')
                        : hasPreview
                          ? t('adminMainPage.agents.voicePreviewAvailable')
                          : t('adminMainPage.agents.noVoicePreview')
                    }
                    arrow
                    placement="top">
                    <Box
                      onClick={canPlay ? handleVoiceIconClick : undefined}
                      className={iconClasses}>
                      {hasPreview ? (
                        <VolumeUpIcon className={classes.volumeIcon} />
                      ) : (
                        <VolumeOffIcon className={classes.volumeIcon} />
                      )}
                    </Box>
                  </Tooltip>
                );
              })()}
              <Box className={classes.voiceSelectWrapper}>
                <VoicePicker
                  voices={filteredVoices}
                  selectedVoiceId={state.voice || ''}
                  disabled={!state.sex || isPlayingVoice}
                  placeholder={t('campaignPage.placeholders.voiceType')}
                  onChange={(voiceId) =>
                    dispatch({
                      type: ActionEnum.SET_VOICE,
                      payload: voiceId,
                    })
                  }
                />
              </Box>
            </Box>
          </Box>
        </CompositeFieldContainer>
      </TableCell>

      <TableCell className={sharedClasses.languageCell}>
        <CustomSelectField
          control={control}
          labelId="language-type"
          name="languageType"
          value={state.language}
          onChange={handleInputChange(ActionEnum.SET_LANGUAGE)}
          placeholder={t('campaignPage.placeholders.languageType')}
          options={accentOptions}
          disabled={isPlayingVoice}
          renderValue={(selected) => {
            const selectedLang = accentOptions?.find((lang) => lang.key === selected);
            return selectedLang?.label || '';
          }}
        />
      </TableCell>

      <TableCell className={sharedClasses.transcriberCell}>
        <CustomSelectField
          control={control}
          labelId="transcriber-provider"
          name="transcriberId"
          value={state.transcriberId}
          onChange={handleInputChange(ActionEnum.SET_TRANSCRIBER)}
          placeholder={t('campaignPage.placeholders.transcriberType')}
          options={transcriberOptions}
          disabled={isPlayingVoice}
          renderValue={(selected) => {
            const selectedTranscriber = transcriberOptions?.find(
              (transcriber) => transcriber.key === selected,
            );
            return selectedTranscriber?.label || '';
          }}
        />
      </TableCell>

      <TableCell className={sharedClasses.llmCell}>
        <CompositeFieldContainer>
          <CustomSelectField
            control={control}
            labelId="llm-provider"
            name="llmProviderId"
            value={selectedProviderId}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value as string;
              setSelectedProviderId(value);
              const provider = llmProviders?.find((p) => p.id === value);
              const allows = provider?.allowsCustomModels ?? false;
              setProviderAllowsCustom(allows);
              // Reset incompatible fields when provider changes
              setCustomModelKey('');
              setSelectedModelId('');
            }}
            placeholder={t('adminMainPage.agents.llmProvider')}
            options={llmProviders?.map((p) => ({ key: p.id, value: p.id, label: p.title })) || []}
            disabled={isPlayingVoice}
            renderValue={(selected) => {
              const selectedProvider = llmProviders?.find((p) => p.id === selected);
              return (
                selectedProvider?.title ||
                agent?.llmProvider?.title ||
                (t('adminMainPage.agents.llmProvider') as string)
              );
            }}
          />

          {selectedProviderId &&
            (providerAllowsCustom ? (
              <CustomTextField
                control={control}
                name="llmCustomModelKey"
                value={customModelKey}
                onChange={(e: any) => setCustomModelKey(e.target.value)}
                id="llm-custom-model-key"
                type="text"
                placeholder={t('adminMainPage.agents.llmCustomModelKey')}
                varient="outlined"
                fullWidth
                required
                disabled={isPlayingVoice}
              />
            ) : (
              <CustomSelectField
                control={control}
                labelId="llm-model"
                name="llmModelId"
                value={selectedModelId}
                onChange={(e) => setSelectedModelId((e.target as HTMLInputElement).value as string)}
                placeholder={t('adminMainPage.agents.llmModel')}
                options={llmModels?.map((m) => ({ key: m.id, value: m.id, label: m.title })) || []}
                disabled={isPlayingVoice}
                renderValue={(selected) => {
                  const selectedModel = llmModels?.find((m) => m.id === selected);
                  return selectedModel?.title || (t('adminMainPage.agents.llmModel') as string);
                }}
              />
            ))}
        </CompositeFieldContainer>
      </TableCell>

      <TableCell className={sharedClasses.profileCell}>
        <RolePicker
          profiles={agentProfileSelectList}
          selected={
            agentProfileSelectList?.find((profile) => profile.id === state?.profile?.id) ?? null
          }
          disabled={isPlayingVoice}
          onChange={(profile) =>
            dispatch({
              type: ActionEnum.SET_PROFILE,
              payload: profile as AgentProfileRequestDto,
            })
          }
        />
      </TableCell>

      <TableCell className={sharedClasses.actionsCell}>
        <Box
          display="flex"
          flexDirection={'column'}
          justifyContent="flex-end"
          alignItems="center"
          gap={1}>
          <button className={classes.save_button} disabled={isSaveDisabled} onClick={handleSave}>
            <CheckIcon htmlColor="white" style={{ width: 16 }} />
          </button>

          <button className={classes.cancel_button} color="secondary" onClick={handleCancel}>
            <CloseIcon htmlColor="#7C8FAC" style={{ width: 16 }} />
          </button>
        </Box>
      </TableCell>
    </>
  );
};
